import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../Layout/LanguageContext';
import {
  FaDatabase,
  FaChartLine,
  FaCogs,
  FaRocket
} from 'react-icons/fa';

const ProjectsSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProjectCard = styled(motion.div)`
  background: var(--background-light);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 51, 102, 0.1);
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 51, 102, 0.15);
    border-color: var(--accent-color);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ProjectHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const ProjectLogo = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid var(--background-dark);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
  }
`;

const ProjectInfo = styled.div`
  flex: 1;
`;

const ProjectTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const ProjectClient = styled.p`
  font-size: 0.9rem;
  color: var(--accent-color);
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProjectContent = styled.div`
  margin-bottom: 1.5rem;
`;

const ProjectSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionLabel = styled.h4`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SectionText = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0;
`;

const TechnologiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const TechnologyTag = styled.span`
  background: var(--background-dark);
  color: var(--primary-color);
  padding: 0.3rem 0.6rem;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    transform: translateY(-2px);
  }
`;



const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 6px;
  height: 6px;
  background: var(--accent-color);
  border-radius: 50%;
  opacity: 0.4;

  &:nth-child(1) { top: 20%; left: 10%; }
  &:nth-child(2) { top: 40%; right: 15%; }
  &:nth-child(3) { top: 60%; left: 20%; }
  &:nth-child(4) { top: 80%; right: 25%; }
`;

const projectsContent = {
  de: {
    title: 'Projekte in der Praxis',
    subtitle: 'Erfolgreiche Fallstudien aus meiner Beratungstätigkeit',
    projects: [
      {
        client: 'Reinke Digital UG (PropTech · Eigenes SaaS-Produkt)',
        title: 'Immokompass – KI-Analyse für Immobilien-Investments',
        logo: '/images/immokompass.png',
        challenge: 'Immobilien-Due-Diligence ist aufwendig, uneinheitlich und erfordert Expertenwissen. Privatinvestoren wie professionelle Akteure brauchen eine schnelle, standardisierte Einschätzung, ob sich ein Objekt als Investment lohnt.',
        solution: 'Als Gründer und Entwickler habe ich Immokompass aufgebaut: eine SaaS-Plattform, die hochgeladene Exposés, Grundrisse, Energieausweise und WEG-Protokolle per KI auswertet und daraus einen standardisierten Investment-Report über sieben feste Abschnitte erzeugt — von Objektdaten und Cashflow-Check bis Standortanalyse und Handlungsempfehlung. Technisches Herzstück ist eine „One-JSON-two-outputs“-Architektur: Das Sprachmodell liefert striktes, schema-konformes JSON, das identische Web- und PDF-Reports speist.',
        result: 'Nutzer erhalten in Minuten eine klare GO-/NO-GO-/WATCHLIST-Einschätzung — pixelgleich als Web- und PDF-Report über jede Analyse hinweg. Die Plattform bedient Privatinvestoren (B2C) ebenso wie Makler, Hausverwaltungen und Family Offices (B2B).',
        technologies: ['React', 'Python', 'AWS Bedrock (Claude Sonnet)', 'Supabase', 'Stripe', 'Hetzner', 'Coolify']
      },
      {
        client: 'Professional Services (Big-Four-Beratungshaus)',
        title: 'Copilot-Agent für Excel-Automatisierung',
        logo: '/images/copilot.png',
        challenge: 'Wiederkehrende Excel-Auswertungen mussten manuell befüllt werden: Daten aus dem internen Fachwissen zusammensuchen, interpretieren und in Vorlagen übertragen — zeitaufwendig, fehleranfällig und schwer zu standardisieren.',
        solution: 'Konzeption und Umsetzung eines KI-Agenten auf Basis von Microsoft Copilot, der über eine angebundene unternehmensinterne Wissensbasis (RAG) mit Fachwissen angereichert ist. Der Agent interpretiert die Anforderungen, zieht die passenden Informationen aus der Wissensbasis und schreibt die Ergebnisse strukturiert in die vorgesehenen Excel-Vorlagen.',
        result: 'Deutlich reduzierter manueller Aufwand, standardisierte Ergebnisse und spürbar beschleunigte wiederkehrende Auswertungen.',
        technologies: ['Microsoft Copilot', 'Copilot Studio', 'Microsoft 365', 'Excel', 'RAG / Wissensbasis']
      },
      {
        client: 'LOYOS BI (SaaS · Business Intelligence)',
        title: 'Automatisierte DATEV-Cloud-Integration',
        logo: '/images/loyos-bi.svg',
        challenge: 'Buchhaltungsdaten aus der DATEV-Cloud sollten als verlässliches Fundament für Power BI Reporting bereitstehen. Und zwar vollautomatisch, schnittstellenkonform und über einen Steuerberater-Zugang mit bis zu ~80 Mandanten skalierbar, statt manuell exportiert und gepflegt.',
        solution: 'Aufbau einer Ende-zu-Ende-Datenpipeline von der DATEV-Cloud (Service „Export Rechnungswesen“) nach Azure SQL. Mehrstufige Orchestrierung in Azure Data Factory lädt per Monats-Trigger alle aktiven Mandanten eines Zugangs vollautomatisch und idempotent. Kompletter OAuth2-PKCE-Flow mit Refresh-Token-Verwaltung im Azure Key Vault und automatischer Token-Erneuerung, dazu großvolumige job-basierte Buchungsabrufe über asynchrones Polling. Durchgängig Managed Identities, schnittstellenkonform inklusive Audit-Log und Access-Token-Widerruf.',
        result: 'Ein einziger Steuerberater-Token schaltet einen kompletten Mandanten-Verbund frei (bis zu ~80 Mandanten), der monatlich vollautomatisch und idempotent geladen wird. Das Ergebnis ist ein aktuelles, verlässliches Datenfundament für Power BI, ganz ohne manuellen Aufwand.',
        technologies: ['Azure Functions', 'TypeScript', 'Azure Data Factory', 'Azure SQL', 'Azure Key Vault', 'Managed Identities', 'OAuth2 / PKCE', 'DATEV API', 'Power BI']
      },
      {
        client: 'Professional Services (PwC)',
        title: 'PWC BIC',
        logo: '/images/pwclogo.png',
        challenge: 'Manueller, intransparenter BIA-Prozess mittels Excel und E-Mail ohne zentrale Datenhaltung.',
        solution: 'Entwicklung einer Power Platform-Anwendung mit Power App als Fragebogen und Power Automate zur Automatisierung der Genehmigungs-Workflows. Anbindung der Enterprise-Architecture-Plattform LeanIX via API.',
        result: 'Vollständige Digitalisierung des BIA-Prozesses, Schaffung von Transparenz und einer zentralen, auditierbaren Datenbasis.',
        technologies: ['Power Apps', 'Power Automate', 'SharePoint Online', 'LeanIX API', 'IT-Risikomanagement']
      },
      {
        client: 'IT-Beratung (Onelake IT Beratung)',
        title: 'Power BI Strategie & Governance',
        logo: '/images/onelakelogo.jpg',
        challenge: 'Fehlende Governance und standardisierte Prozesse im Power BI-Bereich, was zu inkonsistenter Entwicklung und erschwerter Zusammenarbeit im Team führte.',
        solution: 'Übernahme der Gesamtverantwortung für die Power BI-Landschaft. Einführung eines professionellen Entwicklungszyklus mit Azure DevOps und Scrum sowie Implementierung von Git zur Versionskontrolle.',
        result: 'Schaffung einer stabilen, skalierbaren Analytics-Plattform. Deutlich verbesserte Code-Qualität und effizientere Zusammenarbeit durch standardisierte Prozesse und Team-Führung.',
        technologies: ['Power BI', 'Azure DevOps', 'Git', 'Scrum', 'Teamführung', 'Strategieentwicklung']
      },
      {
        client: 'Einzelhandel (toom Baumarkt)',
        title: 'Power Platform Enablement & Governance',
        logo: '/images/toom.png',
        challenge: 'Power-Platform-Initiativen entstanden isoliert in den Fachbereichen; es fehlten klare Prozesse, technische Leitplanken und die Verzahnung mit externen IT-Partnern.',
        solution: 'Als Power Platform Lead Developer konzipierte ich ein Governance-Modell, koordinierte interne Stakeholder und Dienstleister und entwickelte Referenzlösungen samt automatisierter Deployment-Strecken über Power Automate und Azure DevOps.',
        result: 'Eine transparente Delivery-Organisation, beschleunigte Rollouts neuer Power Apps und eine gestärkte Inhouse-Kompetenz, die Use Cases unternehmensweit skalierbar umsetzt.',
        technologies: ['Power Apps', 'Power Automate', 'Power Platform', 'Jira', 'Confluence', 'Governance', 'Stakeholder Management']
      },
             {
         client: 'IT-Beratung (Onelake IT Beratung)',
         title: 'KI-basierte Meeting-Analyse',
         logo: '/images/onelakelogo.jpg',
         challenge: 'Wertvolles Wissen aus Meetings war in unstrukturierten Audiodateien "gefangen" und konnte nicht effizient durchsucht oder ausgewertet werden.',
         solution: 'Entwicklung eines KI-gestützten Chatbots mit Copilot Studio. Ein Power Automate Flow automatisiert die Transkription der Meeting-Audiodateien mittels Azure AI Speech.',
         result: 'Transformation von unstrukturierten Meeting-Inhalten in eine interaktive, durchsuchbare Wissensdatenbank, die per Chatbot in Echtzeit abgefragt werden kann.',
         technologies: ['Copilot Studio', 'Power Automate', 'Azure AI Speech', 'Azure AI Foundry', 'KI-Entwicklung']
       },
       {
         client: 'Hirschvogel (Automotive)',
         title: 'Power BI Performance-Optimierung & Reporting',
         logo: '/images/hirschvogellogo.png',
         challenge: 'Mangelnde Performance von Power BI-Berichten auf Basis großer Datenmengen aus Microsoft Dynamics.',
         solution: 'Entwicklung komplexer SQL-Views zur Datenaggregation und -optimierung. Gezielte Performance-Anpassung von Power BI-Datensätzen und Erstellung von Paginated Reports.',
         result: 'Signifikante Steigerung der Report-Performance und Ermöglichung von automatisiertem, pixel-perfektem Reporting.',
         technologies: ['Power BI', 'Power BI Paginated Reports', 'Power Automate', 'SQL', 'DAX', 'Dynamics 365', 'Performance-Optimierung']
       },

      {
        client: 'Industrie (Ideematec)',
        title: 'Hybrides Data Warehouse für präzise Finanz-KPIs',
        logo: '/images/Ideematec.jpg',
        challenge: 'Anbindung von On-Premise ERP- und Finanzsystemen an eine moderne Cloud-Analytics-Plattform.',
        solution: 'Konzeption eines DWH auf MS SQL Server mit hybrider Datenintegration via Azure Data Factory. Implementierung der Finanzlogik zur Berechnung von EBITDA und Deckungsbeiträgen. Anbindung an Power BI für interaktive Analysen.',
        result: 'Schaffung einer zentralen Datenwahrheit und ermöglichte datengetriebene Finanzsteuerung.',
        technologies: ['MS SQL Server', 'Azure Data Factory', 'Power BI', 'T-SQL', 'ETL']
      },
      {
        client: 'Logistik (STI Logistics)',
        title: 'Effizienzsteigerung durch unternehmensweite Power App',
        logo: '/images/STILogo.jpg',
        challenge: 'Ineffiziente, manuelle Prozesse bei hunderten Mitarbeitern.',
        solution: 'Entwicklung einer zentralen Power App mit SharePoint-Anbindung und automatisierten Workflows via Power Automate.',
        result: 'Messbare Effizienzsteigerung von 5% im gesamten Unternehmen.',
        technologies: ['Power Apps', 'Power Automate', 'SharePoint', 'Microsoft 365']
      },
      {
        client: 'Automotive (Volkswagen)',
        title: 'IT-Kosten-Controlling auf Basis von SAP-Daten',
        logo: '/images/vwlogo.jpg',
        challenge: 'Mangelnde Transparenz über IT-Kosten aus verschiedenen Datenquellen.',
        solution: 'Erstellung und Verwaltung von ETL-Prozessen in Azure Data Factory zur Integration von SAP-Stammdaten. Automatisierung der Datenflüsse und Visualisierung in interaktiven Power BI-Reports.',
        result: 'Umfassende Transparenz und verbesserte Steuerung der IT-Kosten.',
        technologies: ['Azure Data Factory', 'SAP', 'Power BI', 'ETL', 'Data Integration']
      }
    ]
  },
  en: {
    title: 'Projects in Practice',
    subtitle: 'Successful case studies from my consulting work',
    projects: [
      {
        client: 'Reinke Digital UG (PropTech · Own SaaS Product)',
        title: 'Immokompass – AI Analysis for Real Estate Investments',
        logo: '/images/immokompass.png',
        challenge: 'Real estate due diligence is time-consuming, inconsistent and expertise-heavy. Private investors and professionals alike need a fast, standardized assessment of whether a property is worth investing in.',
        solution: 'As founder and developer I built Immokompass: a SaaS platform that uses AI to evaluate uploaded listings, floor plans, energy certificates and HOA minutes and generates a standardized investment report across seven fixed sections — from property data and cashflow check to location analysis and recommended next steps. At its core is a “one-JSON-two-outputs” architecture: the LLM produces strict, schema-conformant JSON that feeds identical web and PDF reports.',
        result: 'Users receive a clear GO / NO-GO / WATCHLIST verdict within minutes — pixel-identical as web and PDF report across every analysis. The platform serves private investors (B2C) as well as brokers, property managers and family offices (B2B).',
        technologies: ['React', 'Python', 'AWS Bedrock (Claude Sonnet)', 'Supabase', 'Stripe', 'Hetzner', 'Coolify']
      },
      {
        client: 'Professional Services (Big Four Consulting Firm)',
        title: 'Copilot Agent for Excel Automation',
        logo: '/images/copilot.png',
        challenge: 'Recurring Excel analyses had to be populated manually: gathering data from internal domain knowledge, interpreting it and transferring it into templates — time-consuming, error-prone and hard to standardize.',
        solution: 'Designed and implemented an AI agent based on Microsoft Copilot, enriched with the company’s internal domain knowledge via a connected knowledge base (RAG). The agent interprets the requirements, pulls the relevant information from the knowledge base and writes the results into the designated Excel templates in a structured way.',
        result: 'Significantly reduced manual effort, standardized outputs and noticeably faster recurring analyses.',
        technologies: ['Microsoft Copilot', 'Copilot Studio', 'Microsoft 365', 'Excel', 'RAG / Knowledge Base']
      },
      {
        client: 'LOYOS BI (SaaS · Business Intelligence)',
        title: 'Automated DATEV Cloud Integration',
        logo: '/images/loyos-bi.svg',
        challenge: 'Accounting data from the DATEV cloud needed to be available as a reliable foundation for Power BI reporting — fully automated, compliant with the interface specifications, and scalable via a tax advisor account with up to ~80 clients, instead of being exported and maintained manually.',
        solution: 'Built an end-to-end data pipeline from the DATEV cloud (service “Export Rechnungswesen”) to Azure SQL. Multi-stage orchestration in Azure Data Factory loads all active clients of an account fully automatically and idempotently via a monthly trigger. Complete OAuth2 PKCE flow with refresh token management in Azure Key Vault and automatic token renewal, plus high-volume job-based accounting record retrieval via asynchronous polling. Managed identities throughout, interface-compliant including audit log and access token revocation.',
        result: 'A single tax advisor token unlocks an entire client network (up to ~80 clients) that is loaded fully automatically and idempotently every month. The result is an up-to-date, reliable data foundation for Power BI — with no manual effort at all.',
        technologies: ['Azure Functions', 'TypeScript', 'Azure Data Factory', 'Azure SQL', 'Azure Key Vault', 'Managed Identities', 'OAuth2 / PKCE', 'DATEV API', 'Power BI']
      },
      {
        client: 'Professional Services (PwC)',
        title: 'Digitalization of Business Impact Analysis (BIA)',
        logo: '/images/pwclogo.png',
        challenge: 'The existing process for assessing the business impact and risk of internal IT applications was manual, slow, and non-transparent. It relied on Excel sheets sent via email, leading to inconsistent data, lack of central overview, and high administrative overhead. The process was not auditable and disconnected from the central enterprise architecture management tool, LeanIX.',
        solution: 'An end-to-end solution was designed and implemented using the Microsoft Power Platform. A user-friendly Power App (Canvas App) guides IT staff through a dynamic risk questionnaire. Power Automate flows orchestrate the entire lifecycle: sending approval requests to managers via Teams/Outlook, managing notifications, and handling data integrations. A key feature was the integration with the LeanIX API. A Power Automate flow fetches the current list of company applications from LeanIX to ensure data consistency. After a BIA is completed and approved, the calculated risk scores are automatically written back to the application record in LeanIX. SharePoint Online was chosen as the cost-effective data backend. To ensure security and confidentiality, a dedicated SharePoint site with restricted access and item-level permissions on the lists was configured, so users can only see their own entries. A concept for a Power BI Dashboard was designed to visualize the BIA results, providing management with a clear overview of the application risk landscape.',
        result: 'The manual process was successfully digitized and automated, drastically reducing the time required for each BIA from days to hours. Data quality and consistency were significantly improved, creating a reliable "Single Source of Truth" for application risk data. The solution provides full transparency and auditability of the entire BIA process. The successful integration with LeanIX enriches the enterprise architecture data with critical risk metrics, improving overall IT governance.',
        technologies: ['Power Apps (Canvas Apps)', 'Power Automate', 'SharePoint Online', 'LeanIX API', 'Power BI', 'Microsoft 365', 'IT Risk Management', 'Enterprise Architecture']
      },
      {
        client: 'IT Consulting (Onelake IT Beratung)',
        title: 'Leadership of Power BI & AI Strategy',
        logo: '/images/onelakelogo.jpg',
        challenge: 'The company needed to professionalize and centralize its internal data analytics capabilities. The existing Power BI development was fragmented, lacking standardized processes for version control, collaboration, and deployment. Furthermore, there was a significant challenge in extracting actionable insights from unstructured data, such as audio recordings of meetings, which remained an untapped resource. The team also required structured leadership to grow its skills.',
        solution: 'A comprehensive strategy for the entire Power BI landscape was established and executed. Process & Governance: A complete development lifecycle was implemented using Azure DevOps and the Scrum methodology. This standardized development, administration, and release management. A key innovation was the introduction of Git for Power BI, enabling robust version control and drastically improving team collaboration. Team Leadership: The role involved leading and mentoring team members, fostering a culture of best practices and continuous improvement in the data analytics domain. AI Innovation: A cutting-edge AI solution was developed to analyze meeting content. This involved: Using Power Automate flows to trigger the transcription of audio files via the Azure AI Speech service. Building intelligent chatbot agents with Copilot Studio. Leveraging the capabilities of Azure AI Foundry to create a system where users could dynamically ask questions about meeting contents in natural language and receive precise answers from the chatbot.',
        result: 'A scalable, secure, and professionally managed Power BI environment was established, positioning data analytics as a core internal asset. The introduction of DevOps and Git practices significantly improved development quality, reduced errors, and accelerated deployment cycles. Team capabilities were strengthened through active leadership and mentoring. The innovative AI solution successfully transformed unstructured audio data into a searchable, interactive knowledge base, increasing efficiency and enabling data-driven insights from meetings.',
        technologies: ['Power BI', 'Azure DevOps', 'Git', 'Scrum', 'Copilot Studio', 'Power Automate', 'Azure AI Foundry', 'Azure AI Speech', 'Team Leadership', 'Strategy Development']
      },
      {
        client: 'Retail (toom DIY Stores)',
        title: 'Power Platform Enablement & Governance',
        logo: '/images/toom.png',
        challenge: 'Power Platform workstreams emerged in silos across business units without a governed lifecycle or structured collaboration with external IT partners.',
        solution: 'As Power Platform Lead Developer I defined the governance framework, aligned internal stakeholders and suppliers, delivered reusable reference apps, and automated deployments via Power Automate and Azure DevOps to guarantee consistent quality.',
        result: 'A transparent operating model, faster rollout of new Power Apps and a strengthened in-house capability that now scales automation use cases end-to-end.',
        technologies: ['Power Apps', 'Power Automate', 'Power Platform', 'Jira', 'Confluence', 'Governance', 'Stakeholder Management']
      },
      {
        client: 'Hirschvogel (Automotive)',
        title: 'Power BI Performance Optimization & Reporting',
        logo: '/images/hirschvogellogo.png',
        challenge: 'The company utilized Power BI for analytics but faced significant performance issues with reports built on large datasets, particularly from Microsoft Dynamics. Existing reports were slow, and there was a need for specialized, print-ready "paginated reports" that the standard Power BI dashboards could not provide. The process for creating and optimizing data models was inefficient, and there was no streamlined workflow for handling new report requests from departments via the Service Desk.',
        solution: 'A multi-pronged approach was taken to address the performance and reporting challenges. Data Layer Optimization: Complex SQL Views were designed and implemented directly in the source database. This allowed for efficient pre-aggregation and transformation of massive datasets before they were even loaded into Power BI, forming the foundation for high-performance reports. Power BI Model Tuning: Existing large-scale Power BI datasets were systematically optimized. This included refining data models, optimizing DAX calculations, and implementing best practices for performance. Advanced Reporting: Power BI Paginated Reports were developed to meet the need for pixel-perfect, printable documents and for seamless integration with Dynamics data exports. Automation & Process: Power Automate was used to automate data refresh cycles and other repetitive tasks. A close and efficient working relationship with the Service Desk was established to ensure new business requirements were translated smoothly into technical solutions. Dashboard Creation: New interactive dashboards and reports were built on top of the optimized data models, enabling business users to perform fast, intuitive data analysis for better decision-making.',
        result: 'The performance of critical Power BI reports was drastically improved, leading to faster load times and a much better user experience. The implementation of paginated reports enabled the automated generation of essential, formatted business documents. Efficient and scalable data processing was achieved through the use of optimized SQL Views, reducing the load on Power BI and ensuring long-term performance. Business departments were empowered with reliable and interactive dashboards, fostering a more data-driven culture.',
        technologies: ['Power BI', 'Power BI Paginated Reports', 'Power Automate', 'SQL', 'DAX', 'Microsoft Dynamics', 'Performance Optimization', 'Data Warehousing']
      },
      {
        client: 'Industry (Ideematec)',
        title: 'Hybrid Data Warehouse for Precise Financial KPIs',
        logo: '/images/Ideematec.jpg',
        challenge: 'Connecting on-premise ERP and financial systems to a modern cloud analytics platform.',
        solution: 'Design of a DWH on MS SQL Server with hybrid data integration via Azure Data Factory. Implementation of financial logic for calculating EBITDA and contribution margins. Connection to Power BI for interactive analyses.',
        result: 'Creation of a central data truth and enabled data-driven financial management.',
        technologies: ['MS SQL Server', 'Azure Data Factory', 'Power BI', 'T-SQL', 'ETL']
      },
      {
        client: 'Logistics (STI Logistics)',
        title: 'Efficiency Increase through Company-wide Power App',
        logo: '/images/STILogo.jpg',
        challenge: 'Inefficient, manual processes among hundreds of employees.',
        solution: 'Development of a central Power App with SharePoint integration and automated workflows via Power Automate.',
        result: 'Measurable efficiency increase of 5% across the entire company.',
        technologies: ['Power Apps', 'Power Automate', 'SharePoint', 'Microsoft 365']
      },
      {
        client: 'Automotive (Volkswagen)',
        title: 'IT Cost Controlling Based on SAP Data',
        logo: '/images/vwlogo.jpg',
        challenge: 'Lack of transparency about IT costs from various data sources.',
        solution: 'Creation and management of ETL processes in Azure Data Factory for integrating SAP master data. Automation of data flows and visualization in interactive Power BI reports.',
        result: 'Comprehensive transparency and improved control of IT costs.',
        technologies: ['Azure Data Factory', 'SAP', 'Power BI', 'ETL', 'Data Integration']
      }
    ]
  }
};

const Projects: React.FC = () => {
  const { language } = useLanguage();
  const t = projectsContent[language];
  // threshold 0: auslösen, sobald der Abschnitt ins Bild kommt. Mit einem Anteil (z. B. 0.1)
  // wird der Abschnitt auf dem Handy nie sichtbar, weil er dort viel höher als der Bildschirm ist.
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <ProjectsSection id="projects" ref={ref}>
      <Container>
        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <ProjectsGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {t.projects.map((project, index) => (
            <ProjectCard key={index} variants={cardVariants}>
              <ProjectHeader>
                <ProjectLogo>
                  <img src={project.logo} alt={project.client} />
                </ProjectLogo>
                <ProjectInfo>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectClient>{project.client}</ProjectClient>
                </ProjectInfo>
              </ProjectHeader>

              <ProjectContent>
                <ProjectSection>
                  <SectionLabel>
                    <FaDatabase />
                    Herausforderung
                  </SectionLabel>
                  <SectionText>{project.challenge}</SectionText>
                </ProjectSection>

                <ProjectSection>
                  <SectionLabel>
                    <FaCogs />
                    Lösung
                  </SectionLabel>
                  <SectionText>{project.solution}</SectionText>
                </ProjectSection>

                <ProjectSection>
                  <SectionLabel>
                    <FaChartLine />
                    Ergebnis
                  </SectionLabel>
                  <SectionText>{project.result}</SectionText>
                </ProjectSection>

                <ProjectSection>
                  <SectionLabel>
                    <FaRocket />
                    Technologien
                  </SectionLabel>
                  <TechnologiesList>
                    {project.technologies.map((tech, techIndex) => (
                      <TechnologyTag key={techIndex}>{tech}</TechnologyTag>
                    ))}
                  </TechnologiesList>
                </ProjectSection>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </Container>

      <FloatingElement
        animate={{
          y: [0, -15, 0],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <FloatingElement
        animate={{
          y: [0, 20, 0],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />
      <FloatingElement
        animate={{
          y: [0, -10, 0],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
      <FloatingElement
        animate={{
          y: [0, 25, 0],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 3,
        }}
      />
    </ProjectsSection>
  );
};

export default Projects;



